export interface ServiceHighlight {
  icon: string;
  title: string;
  text: string;
}

export interface ServiceProcessStep {
  title: string;
  text: string;
}

export interface ServiceFaq {
  q: string;
  a: string;
}

export interface ServiceDetail {
  slug: string;
  crumb: string;
  title: string;
  tagline: string;
  eyebrow: string;
  intro: string[];
  image: string;
  gallery?: string[];
  highlights: ServiceHighlight[];
  includes: string[];
  process: ServiceProcessStep[];
  costFactors: string[];
  faqs: ServiceFaq[];
  areasEyebrow?: string;
  areasHeading?: string;
  areasIntro?: string;
  areas?: { name: string; text: string }[];
}

export const serviceDetails: ServiceDetail[] = [
  {
    slug: "house-shifting",
    crumb: "House Shifting",
    title: "House Shifting Services in Bangalore",
    tagline: "#1 Home Relocation Service in Bangalore Since 2009",
    eyebrow: "Home Relocation",
    intro: [
      "Lahari Packers and Movers is one of the top house shifting service providers in Bangalore, handling everything from a single item move to a complete large household relocation. Our trained crews arrive with quality packing material, wrap every item carefully and deliver it safely to your new home.",
      "From apartments in Nagarabhavi to independent houses in Rajajinagar, Jayanagar or Whitefield, we plan each move around your building rules, lift timings and parking access. You get a clear estimate upfront, on-time arrival and complete unpacking support at your destination.",
    ],
    image: "/uploads/service-house-shifting.jpeg",
    highlights: [
      { icon: "ri-box-3-line", title: "Premium Packing Material", text: "Bubble wrap, foam sheets, corrugated rolls, cartons and wooden crates for safe transit." },
      { icon: "ri-home-smile-2-line", title: "Full Home & Partial Moves", text: "Complete household shifting or just a few selected items — we handle both." },
      { icon: "ri-shield-check-line", title: "Handled With Care", text: "Furniture is dismantled, labelled and re-assembled at your new home." },
      { icon: "ri-calendar-check-line", title: "On-Time Arrival", text: "Confirmed slots with a dedicated crew and vehicle for your shifting date." },
    ],
    includes: [
      "Pre-move survey and item-wise estimate",
      "Dismantling of beds, wardrobes and large furniture",
      "Multi-layer packing for fragile and glassware",
      "TV, fridge and washing machine protection packs",
      "Loading with trolleys, straps and edge guards",
      "Dedicated closed-body truck for your goods",
      "Unloading, unpacking and placement",
      "Re-assembly of furniture at the new home",
    ],
    process: [
      { title: "Free Survey & Quote", text: "Share your home size and items over a call or WhatsApp and receive a transparent estimate." },
      { title: "Packing Day", text: "Our uniformed crew arrives with material, packs room by room and labels every carton." },
      { title: "Safe Shifting", text: "Goods are loaded carefully into a dedicated vehicle and moved to your new address." },
      { title: "Unpack & Settle", text: "We unload, unpack, re-assemble furniture and place items where you want them." },
    ],
    costFactors: [
      "Total volume of household goods",
      "Shifting distance within Bangalore",
      "Floor number and lift availability",
      "Quality of packing material required",
      "Additional manpower for heavy items",
      "Preferred date, weekend or peak-day move",
    ],
    faqs: [
      { q: "How early should I book my house shifting?", a: "We recommend booking 3 to 5 days in advance. For month-end dates we suggest booking a week earlier as slots fill up fast." },
      { q: "Do you dismantle and re-assemble furniture?", a: "Yes. Beds, wardrobes, dining tables and modular units are dismantled before packing and re-assembled at your new home by our technicians." },
      { q: "Is my household goods insured during the move?", a: "Every move is handled by trained staff and can be backed by transit cover. Our team explains the options in your written estimate." },
    ],
  },
  {
    slug: "office-shifting",
    crumb: "Office Shifting",
    title: "Office Shifting Services in Bangalore",
    tagline: "Zero-Downtime Commercial & Corporate Relocation",
    eyebrow: "Commercial Movers",
    intro: [
      "Office shifting needs planning, not just packing. Lahari Packers and Movers relocates offices, startups, clinics and corporate workspaces across Bangalore with minimum disruption to your working hours. We can schedule the move over a weekend or at night so your team walks into a ready workspace on Monday morning.",
      "Our crew is trained to handle IT equipment, server racks, workstations, ergonomic chairs, file cabinets and glass partitions. Every asset is inventoried, coded and matched against your list during loading and delivery.",
    ],
    image: "/uploads/service-office-shifting.jpeg",
    highlights: [
      { icon: "ri-cpu-line", title: "IT & Server Safe Handling", text: "Anti-static wrapping, labelled cabling and crated racks for sensitive equipment." },
      { icon: "ri-calendar-schedule-line", title: "Weekend & Night Moves", text: "We work around your business hours to keep productivity losses near zero." },
      { icon: "ri-file-list-3-line", title: "Asset Inventory", text: "Every item is coded and cross-checked during loading and final placement." },
      { icon: "ri-building-4-line", title: "Multi-Floor Support", text: "Coordinated with building management for lift, dock and access permissions." },
    ],
    includes: [
      "Pre-move site inspection with your admin team",
      "Workstation, monitor and CPU packing",
      "Modular furniture dismantling and re-assembly",
      "Server, UPS and network equipment crating",
      "File cabinet locking and archive boxing",
      "Colour-coded labelling by department",
      "Dedicated fleet with covered loading dock",
      "Post-move debris removal and disposal",
    ],
    process: [
      { title: "Site Survey", text: "We walk the floor with your facility team and map every department, asset and dead-line." },
      { title: "Move Plan", text: "A department-wise sequence and timeline is shared so everyone knows what moves when." },
      { title: "Pack & Shift", text: "Crews pack department by department, load in sequenced vehicles and move to the new office." },
      { title: "Setup & Verify", text: "Items are placed as per the seating plan and verified against the inventory sheet." },
    ],
    costFactors: [
      "Number of workstations and departments",
      "Volume of IT, server and network equipment",
      "Dismantling and re-installation complexity",
      "Floor levels, lift and dock access",
      "Distance between old and new office",
      "Preferred shifting window (day, night or weekend)",
    ],
    faqs: [
      { q: "Can you move our office over a weekend?", a: "Absolutely. Most of our corporate relocations run Friday evening to Sunday night so the workspace is ready on Monday." },
      { q: "How do you handle servers and IT equipment?", a: "We use anti-static wrap, shock-absorbing foam and labelled cable tags. Racks are crated and moved upright with dedicated supervision." },
      { q: "Will you help with publicising the change of address?", a: "Yes, we can include change-of-address notices and support with intimation notices for government departments on request." },
      { q: "Do you provide storage in between two offices?", a: "Yes. Goods can be stored in our secure Bangalore warehouse if your new premises is not ready yet." },
    ],
  },
  {
    slug: "storage",
    crumb: "Storage Service",
    title: "Storage Service in Bangalore",
    tagline: "Secure, Clean & Spacious Own Warehouse Storage",
    eyebrow: "Warehousing & Storage",
    intro: [
      "Not everything has to move at once. Lahari Packers and Movers offers packaging and own warehouse storage service in Bangalore, so your household or office goods stay safe, dry and dust-free until you are ready for them.",
      "Our storage bays are constructed to protect goods from water seepage, humidity and rodents. Every item is packed in sealed cartons, stacked on pallets and recorded with a storage receipt so nothing is misplaced.",
    ],
    image: "/uploads/service-storage.jpeg",
    highlights: [
      { icon: "ri-drop-line", title: "Waterproof Construction", text: "RCC flooring and raised pallets keep goods safe from seepage and dampness." },
      { icon: "ri-shield-keyhole-line", title: "24x7 Monitoring", text: "Secured premises with round-the-clock security and controlled entry." },
      { icon: "ri-scales-3-line", title: "Pest Controlled", text: "Scheduled pest treatment and regular housekeeping across all storage bays." },
      { icon: "ri-file-text-line", title: "Storage Receipt", text: "Item-wise listing on every receipt so your goods are easy to retrieve later." },
    ],
    includes: [
      "Short term and long term storage options",
      "Professional packing before storage intake",
      "Palletised stacking for furniture and cartons",
      "Separate bays for household and commercial goods",
      "Item-wise inventory and storage receipt",
      "Flexible retrieval with prior notice",
      "Delivery from storage to your destination",
      "Cold room and general warehouse options",
    ],
    process: [
      { title: "Book Storage", text: "Tell us the volume and duration. We suggest the right bay and packing plan." },
      { title: "Pack & Intake", text: "Goods are packed, listed on an inventory sheet and placed into your storage bay." },
      { title: "Safe Keeping", text: "Your belongings stay dust-free, dry and monitored until you release it." },
      { title: "Retrieve & Deliver", text: "Give us a date and we deliver the goods to your new home or office." },
    ],
    costFactors: [
      "Total space occupied by your goods",
      "Storage duration (weekly or monthly)",
      "Packing material required before intake",
      "Number of retrieval and delivery trips",
      "Accessibility of the delivery address",
      "Special care for fragile or high-value items",
    ],
    faqs: [
      { q: "What is the minimum storage period?", a: "We accept storage from a few weeks to several months. Short term and long term options are both available at our Bangalore hub." },
      { q: "Can I access my goods while they are in storage?", a: "Yes, with prior notice we can schedule a visit to retrieve specific items from your bay." },
      { q: "Is the warehouse protected from rain and rodents?", a: "Yes. The warehouse is built to prevent water seepage, treated for pests on schedule and kept dust-free through regular housekeeping." },
      { q: "Do you pack the goods before storing them?", a: "Yes. All items are professionally packed before intake so they remain protected for the entire storage duration." },
    ],
  },
  {
    slug: "local-shifting",
    crumb: "Local Shifting",
    title: "Local Shifting Service in Bangalore",
    tagline: "Same-City House Shifting Across Every Major Residential Area of Bangalore",
    eyebrow: "Local & Intra-City Moves",
    intro: [
      "Moving within the city? Lahari Packers and Movers is a trusted local shifting service in Bangalore handling hundreds of same-city moves every year. Whether you are shifting from Nagarabhavi to Jayanagar or from Malleswaram to Whitefield, our local moving crew packs, loads, moves and sets up your home, often within a single day.",
      "We specialise in short-distance local shifting between Bangalore's major residential localities. Because we know the city's traffic patterns, apartment rules, lift timings and parking access, we plan every move for the fastest possible route and the shortest possible downtime for your family.",
    ],
    image: "/uploads/service-local-shifting.jpeg",
    highlights: [
      { icon: "ri-map-pin-2-line", title: "Doorstep To Doorstep", text: "Pickup from your current home and setup at your new address on the same day." },
      { icon: "ri-timer-flash-line", title: "Same-Day Local Moves", text: "Most within-Bangalore shifts are packed, moved and unpacked in a single day." },
      { icon: "ri-route-line", title: "Area Experts", text: "Crews who know the city's shortcuts, building rules, lift timings and parking limits." },
      { icon: "ri-shield-check-line", title: "Fully Equipped Crew", text: "Trained staff, own packing material and dedicated closed-body vehicles for every job." },
    ],
    includes: [
      "Free local survey and area-wise estimate",
      "Multi-layer packing of fragile and glassware",
      "Dismantling of beds, wardrobes and heavy furniture",
      "Loading with trolleys, straps and edge guards",
      "Dedicated closed-body truck for your goods",
      "Same-day moving between Bangalore localities",
      "Unloading, unpacking and placement at the new home",
      "Re-assembly of furniture at the destination",
    ],
    process: [
      { title: "Share Your Locations", text: "Tell us your current and new area in Bangalore, plus a rough list of items, over a call or WhatsApp." },
      { title: "Get A Local Quote", text: "We share a transparent, item-wise estimate for your intra-city move, usually the same day." },
      { title: "Pack & Load", text: "Our crew arrives with material, packs room by room and loads your goods into a dedicated truck." },
      { title: "Deliver & Settle", text: "We drive straight to your new locality, unload, unpack and re-assemble furniture where you want it." },
    ],
    costFactors: [
      "Distance between your old and new Bangalore locality",
      "Total volume of household goods",
      "Floor number and lift availability at both ends",
      "Quality of packing material required",
      "Additional manpower for heavy or bulky items",
      "Preferred date, weekend or month-end move",
    ],
    faqs: [
      { q: "Can my local Bangalore move be completed in one day?", a: "Yes. Most intra-city shifts between Bangalore localities are packed, moved and unpacked the same day, as long as both buildings allow loading and unloading in your booked slot." },
      { q: "How early should I book a local shifting service?", a: "We recommend booking 2 to 3 days ahead. For month-end dates we suggest a week's notice, as slots across Bangalore fill up quickly." },
      { q: "Do you charge extra for distance within Bangalore?", a: "Your estimate is based on the total volume of goods and the distance between your two localities, so there are no surprise add-ons. Everything is confirmed in writing before the move." },
      { q: "Can you handle moving from a high-rise apartment?", a: "Absolutely. We routinely shift families in and out of high-rise apartments in Whitefield, HSR Layout, Koramangala and other areas, coordinating lift and gate permissions where needed." },
      { q: "Do you move just a few items within the city?", a: "Yes. We handle partial local moves, single-item pickups and small office shifts, not only complete households." },
      { q: "Which parts of Bangalore do you cover for local shifting?", a: "We cover all of Bangalore, including Nagarabhavi, Rajajinagar, Malleswaram, Vijayanagar, Jayanagar, JP Nagar, Banashankari, HSR Layout, Koramangala, Whitefield, Marathahalli, Kengeri, RR Nagar and Yelahanka." },
    ],
    areasEyebrow: "Areas We Serve",
    areasHeading: "Local shifting across Bangalore's residential pockets",
    areasIntro: "From West Bangalore to Whitefield, our local moving crews cover every major residential locality in the city. Pick your area to see the kind of local shifting we handle.",
    areas: [
      { name: "Nagarabhavi", text: "Our home base and most active area. Reliable local packers and movers in Nagarabhavi for house shifting, flat moves and same-day storage." },
      { name: "Rajajinagar", text: "Trusted local shifting service in Rajajinagar for apartments, independent houses and small shops across West Bangalore." },
      { name: "Malleswaram", text: "House shifting in Malleswaram with careful handling of antiques, glassware and heritage-home furniture." },
      { name: "Vijayanagar", text: "Local movers in Vijayanagar for quick, budget-friendly shifting of 1 BHK, 2 BHK and 3 BHK homes." },
      { name: "Basaveshwaranagar", text: "Same-city relocation in Basaveshwaranagar covering packing, loading, moving and unpacking in one trip." },
      { name: "Jayanagar", text: "Local packers and movers in Jayanagar for family homes, senior citizens and gated-community apartments." },
      { name: "JP Nagar", text: "Local shifting in JP Nagar with flexible weekend and month-end slots for working families." },
      { name: "Banashankari", text: "House shifting in Banashankari with dedicated trucks and trained crews for South Bangalore households." },
      { name: "HSR Layout", text: "Local movers in HSR Layout for IT professionals, bachelors and startup offices shifting within the sector." },
      { name: "Koramangala", text: "Local shifting in Koramangala handling compact apartments, high-rise lifts and restricted parking zones." },
      { name: "Whitefield", text: "Local packers and movers in Whitefield for villas, tech-park offices and long-commute families." },
      { name: "Marathahalli", text: "Local shifting in Marathahalli for apartments near the Outer Ring Road, with quick loading and unloading support." },
      { name: "Kengeri", text: "Local movers in Kengeri and Mysore Road for affordable same-city house shifting on any budget." },
      { name: "RR Nagar", text: "Local shifting service in Rajarajeshwari Nagar (RR Nagar) for homes, villas and small offices." },
      { name: "Yelahanka", text: "Local packers and movers in Yelahanka for North Bangalore apartments, plots and gated layouts." },
      { name: "Anikethana Nagara", text: "Our home base in Kannahalli — same-day house and flat shifting around Anikethana Nagara, quickly arranged." },
      { name: "Kannahalli", text: "Local shifting around Kannahalli and the Nelamangala Road belt, with our office and warehouse just around the corner." },
      { name: "Giddenahalli", text: "Careful local moving across Giddenahalli's growing residential pockets and nearby layouts." },
      { name: "Seegehalli", text: "New-layout and apartment shifting in Seegehalli, Kannur and the roads towards Thanisandra Road." },
      { name: "Magadi Road", text: "Affordable house, flat and shop shifting along the busy Magadi Road corridor in West Bangalore." },
      { name: "NICE Road", text: "Quick local moves for homes and layouts along the NICE Road belt, from Kengeri to the Mysore Road junction." },
    ],
  },
];

export const getServiceDetail = (slug: string): ServiceDetail | undefined =>
  serviceDetails.find((service) => service.slug === slug);